import { useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FaceVerificationResult {
  match: boolean;
  confidence: number;
  reason: string;
  liveness: boolean;
}

export type ScanningState = "idle" | "searching" | "capturing_1" | "capturing_2" | "verifying" | "success" | "error";

export const useFaceVerification = () => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [scanningState, setScanningState] = useState<ScanningState>("idle");
  const [verificationResult, setVerificationResult] = useState<FaceVerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const startCamera = useCallback(async (videoElement: HTMLVideoElement) => {
    try {
      setScanningState("searching");
      console.log("Starting camera with constraints...");
      
      const constraints = {
        video: { 
          facingMode: "user", 
          width: { ideal: 640 }, 
          height: { ideal: 480 } 
        },
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (firstErr) {
        console.warn("Primary camera constraints failed, trying fallback...", firstErr);
        // Fallback to basic video if complex constraints fail
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }

      videoElement.srcObject = stream;
      streamRef.current = stream;
      videoRef.current = videoElement;
      return true;
    } catch (err: any) {
      console.error("Camera start failed:", err);
      setScanningState("error");
      
      let msg = "Camera access denied. Please allow camera access to verify your identity.";
      if (err.name === "NotAllowedError") {
        msg = "Camera permission blocked. Please enable camera access in your browser settings.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        msg = "No camera found on this device. Face verification requires a camera.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        msg = "Camera is already in use by another application.";
      }
      
      setError(msg);
      return false;
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setScanningState("idle");
  }, []);

  const captureFrame = useCallback((): string | null => {
    const video = videoRef.current;
    if (!video) {
      console.warn("captureFrame: videoRef is null");
      return null;
    }
    
    // Check readyState (HAVE_CURRENT_DATA = 2)
    if (video.readyState < 2) {
      console.warn("captureFrame: video not ready (readyState:", video.readyState, ")");
      return null;
    }

    const canvas = canvasRef.current || document.createElement("canvas");
    canvasRef.current = canvas;
    
    // Ensure video dimensions are valid
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Mirror the image (front camera is mirrored)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    return canvas.toDataURL("image/jpeg", 0.8);
  }, []);

  const verifyFace = useCallback(
    async (referenceImageUrl: string, officerInfo?: { officerId: string; officerName: string; siteId?: string | null }): Promise<FaceVerificationResult> => {
      setIsVerifying(true);
      setError(null);
      setVerificationResult(null);

      try {
        // Capture first frame
        setScanningState("capturing_1");
        
        // Safety: Wait up to 2 seconds for video to be ready if it isn't yet
        let retries = 0;
        while ((!videoRef.current || videoRef.current.readyState < 2) && retries < 20) {
          await new Promise(r => setTimeout(r, 100));
          retries++;
        }

        const liveImageBase64 = captureFrame();
        if (!liveImageBase64) {
          throw new Error("Could not capture face image. Please ensure your face is visible and camera is active.");
        }

        // Wait ~3 seconds then capture a second frame for liveness detection
        // We'll update state during this wait to show user progress
        await new Promise(r => setTimeout(r, 1500));
        setScanningState("capturing_2");
        await new Promise(r => setTimeout(r, 1500));
        
        const liveImageBase64Second = captureFrame();
        
        setScanningState("verifying");

        // Get current GPS position
        let lat: number | null = null;
        let lng: number | null = null;
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            })
          );
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch {
          // GPS not available — continue without coords
        }

        const { data, error: fnError } = await supabase.functions.invoke("verify-face", {
          body: {
            referenceImageUrl,
            liveImageBase64,
            liveImageBase64Second: liveImageBase64Second || undefined,
            officerId: officerInfo?.officerId,
            officerName: officerInfo?.officerName,
            siteId: officerInfo?.siteId,
            lat,
            lng,
          },
        });

        if (fnError) {
          console.error("verify-face error:", fnError);
          let detailedError = "Face verification service error (Status " + (fnError as any).status + ")";
          
          try {
            // Aggressively try to parse the error body from the context
            const errorBody = await (fnError as any).context?.json();
            if (errorBody?.error) detailedError = errorBody.error;
            else if (errorBody?.message) detailedError = errorBody.message;
            else if (fnError.message && fnError.message !== "Edge Function returned a non-2xx status code") {
              detailedError = fnError.message;
            }
          } catch (e) {
            if (fnError.message && fnError.message !== "Edge Function returned a non-2xx status code") {
              detailedError = fnError.message;
            }
          }
          
          throw new Error(detailedError);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        const result: FaceVerificationResult = {
          match: data.match ?? false,
          confidence: data.confidence ?? 0,
          reason: data.reason ?? "Unknown",
          liveness: data.liveness ?? false,
        };

        if (result.match && result.liveness) {
          setScanningState("success");
        } else {
          setScanningState("error");
          setError(result.reason);
        }

        setVerificationResult(result);
        return result;
      } catch (err: any) {
        const errorMsg = err.message || "Face verification failed";
        setError(errorMsg);
        setScanningState("error");
        const failResult: FaceVerificationResult = { match: false, confidence: 0, reason: errorMsg, liveness: false };
        setVerificationResult(failResult);
        return failResult;
      } finally {
        setIsVerifying(false);
      }
    },
    [captureFrame]
  );

  const uploadReferencePhoto = useCallback(
    async (officerId: string): Promise<string | null> => {
      const imageData = captureFrame();
      if (!imageData) return null;

      // Convert base64 to blob
      const base64 = imageData.split(",")[1];
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "image/jpeg" });

      const filePath = `${officerId}/reference.jpg`;

      const { error: uploadError } = await supabase.storage
        .from("face-photos")
        .upload(filePath, blob, { upsert: true, contentType: "image/jpeg" });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        return null;
      }

      const { data: urlData } = supabase.storage.from("face-photos").getPublicUrl(filePath);

      // Add cache-buster to force fresh URL
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      // Update officer record
      await supabase.from("officers").update({ face_photo_url: publicUrl }).eq("id", officerId);

      return publicUrl;
    },
    [captureFrame]
  );

  return {
    isVerifying,
    scanningState,
    verificationResult,
    error,
    startCamera,
    stopCamera,
    captureFrame,
    verifyFace,
    uploadReferencePhoto,
    setError,
    setVerificationResult,
  };
};

